import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, Skeleton } from '@heroui/react';
import { Icon } from '@iconify/react';
import { ErrorFallback } from '@/components/errorBoundary';
import { getNodeTestIdProp } from '@/utils';
import { ShiftLedgerSummaryBody } from './ShiftLedgerSummaryBody';
import type { ShiftSummaryLogic } from './useShiftRegister';

type ShiftLedgerSummaryModalProps = ShiftSummaryLogic;

/**
 * The opened shift's ledger summary (render-only), in whichever state the hook
 * reports. Closing it forgets the shift.
 */
export const ShiftLedgerSummaryModal: React.FC<
    ShiftLedgerSummaryModalProps
> = ({ isOpen, state, onClose }) => {
    const { t } = useTranslation('pages');

    return (
        <Modal.Backdrop
            isOpen={isOpen}
            onOpenChange={(open) => {
                if (!open) {
                    onClose();
                }
            }}
            variant="blur"
        >
            <Modal.Container placement="center" scroll="inside" size="lg">
                <Modal.Dialog {...getNodeTestIdProp('shift-ledger-summary')}>
                    <Modal.CloseTrigger />
                    <Modal.Header>
                        <Modal.Icon>
                            <Icon
                                className="size-5"
                                icon="solar:clock-circle-bold-duotone"
                            />
                        </Modal.Icon>
                        <Modal.Heading>
                            {t('shifts.summary.title')}
                        </Modal.Heading>
                    </Modal.Header>
                    <Modal.Body>
                        {state.status === 'loading' ? (
                            <div
                                aria-busy
                                className="flex flex-col gap-3"
                                {...getNodeTestIdProp(
                                    'shift-ledger-summary-skeleton',
                                )}
                            >
                                <Skeleton className="h-16 w-full rounded-xl" />
                                <Skeleton className="h-32 w-full rounded-xl" />
                            </div>
                        ) : null}
                        {state.status === 'error' ? (
                            <ErrorFallback
                                data-testid="shift-ledger-summary-error"
                                id="shift-ledger-summary-error"
                                message={t('shifts.summary.errorMessage')}
                                onRetry={state.onRetry}
                                retryLabel={t('shifts.errorRetry')}
                                title={t('shifts.summary.errorTitle')}
                            />
                        ) : null}
                        {state.status === 'ready' ? (
                            <div className="flex flex-col gap-4">
                                {state.refresh === 'error' ? (
                                    <ErrorFallback
                                        data-testid="shift-ledger-summary-refresh-error"
                                        id="shift-ledger-summary-refresh-error"
                                        message={t(
                                            'shifts.summary.refreshErrorMessage',
                                        )}
                                        onRetry={state.onRetry}
                                        retryLabel={t('shifts.errorRetry')}
                                        title={t('shifts.summary.errorTitle')}
                                    />
                                ) : null}
                                <ShiftLedgerSummaryBody summary={state.data} />
                            </div>
                        ) : null}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            {...getNodeTestIdProp('shift-ledger-summary-close')}
                            onPress={onClose}
                            variant="outline"
                        >
                            {t('shifts.summary.close')}
                        </Button>
                    </Modal.Footer>
                </Modal.Dialog>
            </Modal.Container>
        </Modal.Backdrop>
    );
};
